// The submit state machine for a new situation report. Two backend calls —
// create the incident, then attach media (photos + optional video) — with
// idempotent create retries and a media-only retry path that can never
// duplicate the incident.
//
// Local useReducer on purpose: the shared useStateHook store dedupes by
// reference equality and is the documented render-loop trap for flows that
// write fresh objects every render.
import { useCallback, useReducer, useRef } from 'react';

import { ISOBSERVER, ISSUPPORTER } from '../../../constants/global';
import type { GeoPosition } from '../../../hooks/utils/geolocation';
import { type ICreateIncidentRequest, type IIncident, MAX_VIDEO_BYTES } from '../../../interface/vesr';
import { createIncident, rememberReportedBy, uploadIncidentImages } from '../../../lib/vesr-api';
import { formatBytes } from '../../utils/helper';
import type { IFileUploadStatus } from '../../utils/multiple-upload-document';
import { fileKey } from '../../utils/multiple-upload-document';
import { dualWriteSitrep, type IReportContext } from './utils';

export type SubmitPhase = 'idle' | 'creating' | 'uploading' | 'done';

/**
 * Shown when the server rejects a file for size and gives us nothing to quote —
 * a 413 from Kestrel or an edge proxy has an empty body. Derived from the
 * constant so it tracks MAX_VIDEO_BYTES rather than restating it.
 */
export const TOO_LARGE_MESSAGE = `That video is too large to upload. Keep it under ${formatBytes(MAX_VIDEO_BYTES)}.`;

/**
 * api.vesr's own check (IncidentService), phrased for a human and already
 * naming the real cap — worth quoting back to the agent verbatim.
 */
const SERVICE_SIZE_MESSAGE = /exceeds maximum size/i;

/**
 * ASP.NET's request-size failure. Measured against staging 2026-08-22: this
 * does NOT arrive as a 413. `[RequestSizeLimit]` throws during form binding,
 * `[ApiController]` converts that to a **400 ProblemDetails**, and the text
 * sits in `errors[""][0]`:
 *   "Failed to read the request form. Request body too large.
 *    The max request body size is 110000000 bytes."
 * Never shown to the agent — it quotes a raw byte count.
 */
const FRAMEWORK_SIZE_MESSAGE = /request body too large|failed to read the request form/i;

/**
 * Is this a size rejection, i.e. terminal rather than retryable?
 *
 * Three shapes, because three layers can refuse the body — and only one of
 * them is the 413 you would expect:
 *  - 413 — an edge proxy (nginx `proxy-body-size`), which really does 413.
 *  - 400 + framework text — Kestrel's RequestSizeLimit, via ProblemDetails.
 *  - 400 + service text — api.vesr's own ArgumentException.
 */
export const isSizeRejection = (status?: number, message?: string) =>
	status === 413 || (status === 400 && (SERVICE_SIZE_MESSAGE.test(message || '') || FRAMEWORK_SIZE_MESSAGE.test(message || '')));

/** Prefer the server's wording only when it was written for a human. */
export const sizeRejectionMessage = (message?: string) =>
	message && SERVICE_SIZE_MESSAGE.test(message) ? message : TOO_LARGE_MESSAGE;

export interface ICreateError {
	message: string;
	/** 429 — disable Send and count down. */
	retryAfterSeconds?: number;
	/** Network/offline — keep the form, offer manual retry with the same key. */
	offline?: boolean;
}

interface ISubmitState {
	phase: SubmitPhase;
	incident: IIncident | null;
	fileStatuses: Record<string, IFileUploadStatus>;
	createError: ICreateError | null;
	/** Transient failures — retrying the same bytes CAN succeed. */
	failedFiles: File[];
	/**
	 * Terminal rejections (too large). Deliberately separate from failedFiles:
	 * these must never reach retryPhotos, because re-sending identical bytes to
	 * the same cap can only fail again. The user has to replace the file.
	 */
	rejectedFiles: File[];
}

const initialState: ISubmitState = {
	phase: 'idle',
	incident: null,
	fileStatuses: {},
	createError: null,
	failedFiles: [],
	rejectedFiles: [],
};

type Action =
	| { type: 'creating' }
	| { type: 'created'; incident: IIncident }
	| { type: 'create_failed'; error: ICreateError }
	| { type: 'uploading'; statuses: Record<string, IFileUploadStatus> }
	| { type: 'file_status'; key: string; status: IFileUploadStatus }
	| { type: 'finished'; incident: IIncident | null; failedFiles: File[]; rejectedFiles: File[] }
	| { type: 'reset' };

const reducer = (state: ISubmitState, action: Action): ISubmitState => {
	switch (action.type) {
		case 'creating':
			return { ...state, phase: 'creating', createError: null };
		case 'created':
			return { ...state, incident: action.incident, createError: null };
		case 'create_failed':
			return { ...state, phase: 'idle', createError: action.error };
		case 'uploading':
			return { ...state, phase: 'uploading', fileStatuses: action.statuses, failedFiles: [], rejectedFiles: [] };
		case 'file_status':
			return { ...state, fileStatuses: { ...state.fileStatuses, [action.key]: action.status } };
		case 'finished':
			return {
				...state,
				phase: 'done',
				incident: action.incident ?? state.incident,
				failedFiles: action.failedFiles,
				rejectedFiles: action.rejectedFiles,
			};
		case 'reset':
			return initialState;
		default:
			return state;
	}
};

export interface ISubmitArgs {
	request: ICreateIncidentRequest;
	files: File[];
	/** Optional video clip — uploaded LAST (largest, most failure-prone). */
	video: File | null;
	coords: GeoPosition | null;
	ctx: IReportContext;
	/** Free-text note — used for the citizens-agent dual-write body. */
	description: string;
}

export const useIncidentSubmit = () => {
	const [state, dispatch] = useReducer(reducer, initialState);
	// One idempotency key per LOGICAL submission: reused across create retries
	// (the server replays the cached 2xx), regenerated only on reset.
	const idempotencyKeyRef = useRef<string | null>(null);
	const argsRef = useRef<ISubmitArgs | null>(null);
	const dualWrittenRef = useRef(false);
	// Survives the `uploading` dispatch, which clears rejectedFiles from state
	// before a retry can read it.
	const rejectedRef = useRef<File[]>([]);

	const uploadFiles = useCallback(async (incident: IIncident, files: File[]) => {
		// One request per file — genuine per-card status + per-file retry, and
		// each photo is small enough that batching buys nothing.
		const statuses: Record<string, IFileUploadStatus> = {};
		files.forEach((f) => {
			statuses[fileKey(f)] = { phase: 'pending', progress: 0 };
		});
		dispatch({ type: 'uploading', statuses });

		let latest: IIncident | null = incident;
		const failed: File[] = [];
		const rejected: File[] = [];
		for (const file of files) {
			const key = fileKey(file);
			dispatch({ type: 'file_status', key, status: { phase: 'uploading', progress: 0 } });
			const out = await uploadIncidentImages(incident.id, [file], (percent) => {
				dispatch({ type: 'file_status', key, status: { phase: 'uploading', progress: percent } });
			});
			if (out.reachable && out.httpStatus >= 200 && out.httpStatus < 300) {
				latest = out.body ?? latest;
				dispatch({ type: 'file_status', key, status: { phase: 'success', progress: 100 } });
			} else if (out.reachable && out.httpStatus === 400 && /maximum 1 video/i.test(out.errorMessage || '')) {
				// The video already attached on an earlier attempt (e.g. a timeout
				// after the server succeeded) — a retry trips the server's 1-video
				// cap. That IS success: don't fail the card or re-queue the file.
				dispatch({ type: 'file_status', key, status: { phase: 'success', progress: 100 } });
			} else if (out.reachable && isSizeRejection(out.httpStatus, out.errorMessage)) {
				// Terminal: the file is larger than the server accepts. Retrying
				// re-uploads identical bytes into the same cap, so this must NOT
				// join `failed` — the agent has to swap the file for a smaller one.
				rejected.push(file);
				dispatch({
					type: 'file_status',
					key,
					status: { phase: 'error', progress: 0, error: sizeRejectionMessage(out.errorMessage) },
				});
			} else {
				failed.push(file);
				const message =
					out.reachable && out.httpStatus === 429 ? 'Rate limited — wait a moment, then retry.' : 'Upload failed. Tap retry.';
				dispatch({ type: 'file_status', key, status: { phase: 'error', progress: 0, error: message } });
			}
		}
		return { latest, failed, rejected };
	}, []);

	const runDualWrite = useCallback((args: ISubmitArgs, incident: IIncident | null) => {
		// Best-effort, exactly once per logical submission — VESR is the source
		// of truth; the citizens-agent pipeline failure is contained. Observers and
		// Independent Supporters are skipped: that pipeline feeds agent coverage and
		// cannot tell them apart from party agents.
		if (dualWrittenRef.current || !args.coords || ISOBSERVER() || ISSUPPORTER()) return;
		dualWrittenRef.current = true;
		const mediaUrls = (incident?.attachments ?? []).map((a) => a.fileUrl).filter(Boolean);
		void dualWriteSitrep(args.ctx, args.request.title, args.description, args.coords, mediaUrls);
	}, []);

	const submit = useCallback(
		async (args: ISubmitArgs) => {
			argsRef.current = args;
			if (!idempotencyKeyRef.current) idempotencyKeyRef.current = crypto.randomUUID();

			let incident = state.incident;
			if (!incident) {
				dispatch({ type: 'creating' });
				if (typeof navigator !== 'undefined' && !navigator.onLine) {
					dispatch({
						type: 'create_failed',
						error: { message: 'You’re offline — your report is kept here. Retry when you’re connected.', offline: true },
					});
					return;
				}
				const out = await createIncident(args.request, idempotencyKeyRef.current);
				if (!out.reachable) {
					dispatch({
						type: 'create_failed',
						error: { message: 'Could not reach the situation room. Your report is kept here — retry shortly.', offline: true },
					});
					return;
				}
				if (out.httpStatus === 429) {
					dispatch({
						type: 'create_failed',
						error: { message: 'Too many requests right now.', retryAfterSeconds: out.retryAfterSeconds ?? 60 },
					});
					return;
				}
				if (out.httpStatus < 200 || out.httpStatus >= 300 || !out.body) {
					dispatch({
						type: 'create_failed',
						error: { message: out.errorMessage || 'The report was rejected. Review the details and try again.' },
					});
					return;
				}
				incident = out.body;
				rememberReportedBy(incident.reportedBy);
				dispatch({ type: 'created', incident });
			}

			// Media — independently retryable; the incident is never re-created.
			// Photos first (small, fast feedback), the video last.
			const media = args.video ? [...args.files, args.video] : args.files;
			if (media.length) {
				const { latest, failed, rejected } = await uploadFiles(incident, media);
				runDualWrite(args, latest);
				rejectedRef.current = rejected;
				dispatch({ type: 'finished', incident: latest, failedFiles: failed, rejectedFiles: rejected });
			} else {
				runDualWrite(args, incident);
				dispatch({ type: 'finished', incident, failedFiles: [], rejectedFiles: [] });
			}
		},
		[state.incident, uploadFiles, runDualWrite]
	);

	/** Retry the failed media (photos and/or video) — attaches to the EXISTING incident. */
	const retryPhotos = useCallback(async () => {
		const incident = state.incident;
		const failed = state.failedFiles;
		if (!incident || !failed.length) return;
		const { latest, failed: stillFailed, rejected } = await uploadFiles(incident, failed);
		// Carry forward earlier rejections: this retries only `failed`, so anything
		// already refused for size is not re-attempted and must not drop out of the
		// receipt banner just because an unrelated photo succeeded on retry.
		// A rejected file never sits in `failed`, so a later retry cannot re-produce
		// it — the ref has to accumulate or the second retry drops it again.
		rejectedRef.current = [...rejectedRef.current, ...rejected];
		dispatch({
			type: 'finished',
			incident: latest,
			failedFiles: stillFailed,
			rejectedFiles: rejectedRef.current,
		});
	}, [state.incident, state.failedFiles, uploadFiles]);

	/** Retry a create that failed (same idempotency key — no duplicates). */
	const retrySubmit = useCallback(() => {
		if (argsRef.current) void submit(argsRef.current);
	}, [submit]);

	/** Start a brand-new logical submission ("File another"). */
	const reset = useCallback(() => {
		idempotencyKeyRef.current = null;
		argsRef.current = null;
		dualWrittenRef.current = false;
		rejectedRef.current = [];
		dispatch({ type: 'reset' });
	}, []);

	return { ...state, submit, retrySubmit, retryPhotos, reset };
};
